<script>
    import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
    import { projectState } from "@app/store/projectStore.svelte.js";
    import { lockState } from "@app/store/lockStore.svelte.js";
    import { styleState } from "@app/store/styleStore.svelte.js";
    import { playerState, updatePreviewEvent} from "@app/store/playerStore.svelte.js";    
    import { onMount } from "svelte";
    import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
    import {
        WEB_SPEECH_PROVIDER,
        cancel as cancelWebSpeech,
        speak as speakWebSpeech,
    } from "@app/lib/audio/webSpeechTts.js";

    let audioPlayer,
        playingEventId = null,
        preloadedEventId = null,
        activeWebSpeechEventId = null,
        webSpeechPlaybackToken = 0;

    onMount(() => {
        player.addEventListener("seeked", playerSeekedFunc);
        player.addEventListener("play", playerPlayFunc);
        player.addEventListener("ended", playerEndedFunc);

        return () => {
            player.removeEventListener("seeked", playerSeekedFunc);
            player.removeEventListener("play", playerPlayFunc);
            player.removeEventListener("ended", playerEndedFunc);
            cancelWebSpeech();
        };
    });

    function playerPlayFunc(){
        /* Force the playback effect to re-sync from the current time on resume. */
        playingEventId = null;
        preloadedEventId = null;
    }

    function playerEndedFunc() {
        /* Leave audio alone while a Preview owns it. */
        if (!playerState.previewPlaying) {
            pauseAudioPlayer();
            clearAudioSrc();
        }
        playingEventId = null;
        preloadedEventId = null;
    }

    function playerSeekedFunc(){
        /* Reset tracking so the playback effect re-syncs the in-file offset for the new time.
           Skip the audio teardown while a Preview owns the audio (its seek must not
           cancel the independent preview voice). */
        if (!playerState.previewPlaying) {
            pauseAudioPlayer();
            clearAudioSrc();
        }
        playingEventId = null;
        preloadedEventId = null;
    }

    function getEventByTime(sec){
        if (!eventGroupState[projectState.selected]) {
            return null;
        }

        try {
            return eventGroupState[projectState.selected].events.find(event=>{
                return event.start !== false &&
                    event.end !== false &&
                    event.start <= sec &&
                    event.end >= sec &&
                    isPlayableAudioEvent(event);
            }) || null;
        } catch (err) {
            console.error("Error in getEventByTime:", err);
            console.log(err.message);
            return null;
        }
        
    }

    function getNextPlayableEvent(sec){
        const group = eventGroupState[projectState.selected];
        if (!group) {
            return null;
        }

        try {
            let closestEvent = null;
            for (const event of group.events) {
                if (
                    event.start !== false &&
                    event.start > sec &&
                    isPlayableAudioEvent(event)
                ) {
                    if (!closestEvent || event.start < closestEvent.start) {
                        closestEvent = event;
                    }
                }
            }
            return closestEvent;
        } catch (err) {
            console.error("Error in getNextPlayableEvent:", err);
            console.log(err.message);
            return null;
        }
    }

    function pauseAudioPlayer() {
        cancelWebSpeech();
        activeWebSpeechEventId = null;
        webSpeechPlaybackToken += 1;

        try {
            audioPlayer.pause();
        } catch (err) {
            console.log(err.message);
            audioPlayer = document.getElementById("adPlayer");
        }
    }

    function loadAudioSrc(audioFilePath) {
        try {
            audioPlayer.src = audioFilePath;
            audioPlayer.load();
        } catch(err){
            console.log("Load Source Error: " + err.message);
            audioPlayer = document.getElementById("adPlayer");
        }
        
    }

    function isWebSpeechEvent(audioEvent) {
        return audioEvent?.voice?.provider === WEB_SPEECH_PROVIDER;
    }

    function isPlayableAudioEvent(audioEvent) {
        if (isWebSpeechEvent(audioEvent)) {
            return convertToPlainText(audioEvent?.text || "", " ").trim().length > 0;
        }

        return Boolean(audioEvent?.audioFile);
    }

    function loadAudioEvent(audioEvent) {
        if (isWebSpeechEvent(audioEvent)) {
            clearAudioSrc({ cancelSpeech: false });
            return;
        }

        if (!audioEvent?.audioFile) {
            return;
        }

        loadAudioSrc(audioEvent.audioFile);
    }

    function clearAudioSrc({ cancelSpeech = true } = {}) {
        if (cancelSpeech) {
            cancelWebSpeech();
            activeWebSpeechEventId = null;
            webSpeechPlaybackToken += 1;
        }

        try {
            audioPlayer.pause();
            audioPlayer.removeAttribute("src");
        } catch (err) {
            console.log("Load Source Error: " + err.message);
            audioPlayer = document.getElementById("adPlayer");
        }
    }

    async function playAudioPlayer() {
        try {
            if (!audioPlayer.src) {
                console.warn("Audio source is not set. Cannot play audio.");
                return;
            }

            await audioPlayer.play();
        } catch (err) {
            console.log(err.message);
            audioPlayer = document.getElementById("adPlayer");
        }
    }

    async function playWebSpeechEvent(audioEvent) {
        if (activeWebSpeechEventId === audioEvent?.id) return;

        const text = convertToPlainText(audioEvent?.text || "", " ").trim();
        if (!text) return;

        const playbackToken = webSpeechPlaybackToken + 1;
        webSpeechPlaybackToken = playbackToken;
        activeWebSpeechEventId = audioEvent?.id;

        cancelWebSpeech();
        try {
            await speakWebSpeech(text, {
                voiceURI: audioEvent.voice.voiceURI,
                rate: parseFloat(audioEvent.rate || 1),
                pitch: parseFloat(audioEvent.pitch ?? 1),
                volume: playerState.adVolume,
            });
        } finally {
            if (webSpeechPlaybackToken === playbackToken) {
                activeWebSpeechEventId = null;
            }
        }
    }

    function playAudioEvent(audioEvent) {
        if (isWebSpeechEvent(audioEvent)) {
            playWebSpeechEvent(audioEvent).catch((err) => {
                console.log(err?.message || err);
            });
            return;
        }

        playAudioPlayer();
    }

    function decToHex(value) {
        return Math.floor((value / 100) * 255)
            .toString(16)
            .padStart(2, "0");
    }

    function updateAdPreviewEvent(eventIndex) {
        try {
            if (!playerState.playing){
                updatePreviewEvent(eventIndex);
                selectEvent();
            } 
        } catch (err) {
            console.error("Error in updateAdPreviewEvent:", err);
            console.log(err.message);
        }               
    }    
    
    function selectEvent() {
        if (!eventGroupState[projectState.selected] || eventGroupState[projectState.selected].events.length === 0) {
            return;
        }

        try {
            /* Scroll to and select the event if caption lock is enabled */
            if (lockState.caption && playerState.previewEvent !== -1 && eventGroupState[projectState.selected].selected[0] !== playerState.previewEvent){
                eventGroupState[projectState.selected].selected = [playerState.previewEvent];

                const selectedIndex = eventGroupState[projectState.selected].selected[0];
                
                // Dispatch custom event to scroll to the selected event
                window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                    detail: { index: selectedIndex }
                }));

            } else if (lockState.caption && playerState.previewEvent !== -1){
                const selectedIndex = eventGroupState[projectState.selected].selected[0];
                
                // Dispatch custom event to scroll to the selected event
                window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                    detail: { index: selectedIndex }
                }));
            }
        } catch (err) {
            console.error("Error in selectEvent:", err);
            console.log(err.message);
        }        
    }

/* Stateless playback detection: re-derive the active audio event from the
   current time every tick instead of relying on a multi-tick queue. */
$effect(() => {
    const time = playerState.time;
    const playing = playerState.playing;

    try {
        if (!playing) {
            if (playingEventId !== null) {
                /* Leave audio alone while a Preview owns it (e.g. the user
                   paused the video to selectively uncouple it from the voice). */
                if (!playerState.previewPlaying) {
                    pauseAudioPlayer();
                    clearAudioSrc();
                }
                playingEventId = null;
            }
            preloadedEventId = null;
            return;
        }

        const activeEvent = getEventByTime(time);

        if (activeEvent) {
            if (activeEvent.id !== playingEventId) {
                /* When an event Preview owns the audio, skip AD playback here so
                   we don't double-play; still track the caption overlay/scroll. */
                if (!playerState.previewPlaying) {
                    /* Skip the reload if this event was already preloaded during the gap. */
                    if (preloadedEventId !== activeEvent.id) {
                        loadAudioEvent(activeEvent);
                    }

                    if (!isWebSpeechEvent(activeEvent)) {
                        audioPlayer.currentTime = Math.max(0, time - activeEvent.start);
                    }

                    playAudioEvent(activeEvent);
                }

                playingEventId = activeEvent.id;
                preloadedEventId = null;

                updatePreviewEvent(
                    eventGroupState[projectState.selected].events.findIndex(
                        (event) => event.id === activeEvent.id,
                    ),
                );

                selectEvent();
            }
        } else {
            if (playingEventId !== null) {
                if (!playerState.previewPlaying) {
                    pauseAudioPlayer();
                    clearAudioSrc();
                }
                playingEventId = null;
                updatePreviewEvent(-1);
            }

            /* Preload the nearest upcoming event so it is buffered before its
               start. Skipped while a Preview owns the audio. */
            if (!playerState.previewPlaying) {
                const nextEvent = getNextPlayableEvent(time);
                if (
                    nextEvent &&
                    nextEvent.id !== preloadedEventId &&
                    !isWebSpeechEvent(nextEvent)
                ) {
                    loadAudioEvent(nextEvent);
                    preloadedEventId = nextEvent.id;
                }
            }
        }
    } catch (err) {
        console.error("Error in AdPreview playback effect:", err);
        console.log(err.message);
    }
});

$effect(() => {
    const adVolume = playerState.adVolume;

    if (audioPlayer) {
        audioPlayer.volume = adVolume;
    }
});

$effect(() => {
    // console.log("EFFECT Updating Ad Preview Event...");
    updateAdPreviewEvent(eventGroupState[projectState.selected]?.selected[0]);
});
</script>

<audio bind:this={audioPlayer} id="adPlayer" class="d-none" preload="auto">
    <source src="" type="audio/mpeg">
</audio>

{#if lockState.preview && eventGroupState[projectState.selected]?.events[playerState.previewEvent]}
    <div
        id="SubtitlePreviewWrapper"
        class="d-flex justify-content-start align-items-end"
    >
        <div
            id="SubtitlePreview"
            class="textPreview"
            role="region"
            aria-live="polite"
            style="direction: {eventGroupState[projectState.selected].rtl
                ? 'rtl'
                : 'ltr'}; font-family: {styleState.ccMode
                ? eventGroupState[projectState.selected]?.fontFamily
                : styleState.fontFamily}; font-size: {styleState.ccMode
                ? eventGroupState[projectState.selected]?.fontSize
                : styleState.fontSize}px; color: {styleState.ccMode ? "#ffffff" : styleState.color +
                decToHex(styleState.opacity)};
                background: {styleState.ccMode ? '#000000' : styleState.outlineColor};
                line-height: 110%;
                letter-spacing: {styleState.ccMode ? 4 : styleState.spacing}px;
                {styleState.borderStyle == '1' && !styleState.ccMode
                ? `text-shadow: ${styleState.shadow}px ${styleState.shadow}px 3px ${styleState.shadowColor + decToHex(styleState.shadowOpacity)}; 
      -webkit-text-stroke: ${styleState.outline}px ${styleState.outlineColor + decToHex(styleState.outlineOpacity)};
      background: none;`
                : !styleState.ccMode ? `background: ${styleState.outlineColor + decToHex(styleState.outlineOpacity)}; padding: ${styleState.outline}px` : ''};
                text-align: {eventGroupState[projectState.selected].events[
                playerState.previewEvent
            ].alignment};">
            {@html eventGroupState[projectState.selected].events[
                playerState.previewEvent
            ].text}
        </div>
    </div>
{/if}

<style>
    #SubtitlePreviewWrapper {
        z-index : 9999;
        position: absolute;
        pointer-events: none;
        overflow: hidden;
        height: 100%;
        width: 100%;
        top: 0;
    }

    #SubtitlePreview {
        user-select: none;
    }
</style>
